import { useState } from 'react';
import { useRoute } from './useRoute';
import type { RouteMode, RouteCoords } from './useRoute';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';

interface RouteBadgeProps {
  origin: RouteCoords;
  dest: RouteCoords;
  initialMode?: RouteMode;
  compact?: boolean;
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${meters}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}시간 ${remaining}분` : `${hours}시간`;
}

function formatKRW(amount: number): string {
  return `₩${amount.toLocaleString('ko-KR')}`;
}

const MODE_LABEL: Record<RouteMode, string> = {
  walk: '도보',
  car: '차량',
};

export function RouteBadge({ origin, dest, initialMode = 'walk', compact = false }: RouteBadgeProps) {
  const [mode, setMode] = useState<RouteMode>(initialMode);
  const { data, isLoading, isError } = useRoute(origin, dest, mode);

  const hasRouteData =
    data != null &&
    Number.isFinite(data.durationSeconds) &&
    Number.isFinite(data.distanceMeters);

  function buildLabel(): string {
    if (!data) return '';
    const duration = formatDuration(data.durationSeconds);
    const distance = formatDistance(data.distanceMeters);
    const base = `${MODE_LABEL[mode]} ${duration} · ${distance}`;
    if (mode === 'car' && data.taxiFare != null) {
      return `${base} · 택시 ${formatKRW(data.taxiFare)}`;
    }
    return base;
  }

  if (compact) {
    return (
      <span className="inline-flex items-center whitespace-nowrap text-[10px] font-medium leading-none tracking-[-0.35px] text-ink-faint">
        {isLoading ? (
          '계산 중'
        ) : data && hasRouteData ? (
          `${MODE_LABEL[mode]} ${formatDuration(data.durationSeconds)}`
        ) : isError || (data && !hasRouteData) ? (
          '경로 오류'
        ) : (
          '이동'
        )}
      </span>
    );
  }

  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      {/* Mode toggle */}
      <div
        role="group"
        aria-label="이동 수단 선택"
        className="inline-flex rounded-lg border border-surface-border overflow-hidden"
      >
        {(['walk', 'car'] as RouteMode[]).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={`px-3 py-1 text-small font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:shadow-focus-ring ${
              mode === m
                ? 'bg-ink text-surface'
                : 'bg-surface text-ink-muted hover:bg-surface-subtle'
            }`}
          >
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>

      {/* Route info */}
      {isLoading && <LoadingSpinner size="sm" label="경로 계산 중…" />}


      {(isError || (data && !hasRouteData)) && (
        <span className="text-small text-error" role="alert">
          경로 정보를 불러오지 못했습니다
        </span>
      )}

      {data && hasRouteData && !isLoading && (
        <span className="text-body-md font-medium text-ink" aria-live="polite">
          {buildLabel()}
        </span>
      )}
    </div>
  );
}
